import { handleActions } from 'redux-actions';
import { LOCATION_CHANGE, replace } from 'react-router-redux';
import { getFormValues } from 'redux-form';
import qs from 'qs';
import moment from 'moment';

import _isEmpty from 'lodash/isEmpty';
import _forEach from 'lodash/forEach';
import _get from 'lodash/get';
import _cloneDeep from 'lodash/cloneDeep';
import _range from 'lodash/range';

import http from '../../shared/http';
import endpoints from '../../../config/endpoints';
import intl from '../../shared/intl';
import checkPermissions from '../../shared/check-permissions';
import createTable from '../components/table';
import createFilters from '../../shared/filters';
import { default as createCalendar, getDateRangeFromCalendar } from '../../shared/calendar';

const table = createTable({
	namespace: 'applicantsList',
	stateNodeName: 'table',
	configureSort: {
		type: 'remote',
		resolverSortFn: request
	}
});

const filters = createFilters({
	namespace: 'applicantsList',
	stateNodeName: 'filters',
	queryBuildFunc: () => '',
	listOfFilters: [{
		name: 'managers',
		title: 'Managers',
		placeholder: 'Add managers',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'dynamic',
			source: `${endpoints.APPLICANTS.FILTERS.MANAGERS}`
		}
	}, {
		name: 'countries',
		title: 'Applicant country',
		placeholder: 'Add applicant country',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'dynamic',
			source: endpoints.APPLICANTS.FILTERS.COUNTRIES
		}
	}]
});

const calendar = createCalendar({
	namespace: 'applicantsList',
	stateNodeName: 'calendar',
	defaultValue: {
		start: moment('2019-01-01').format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

const FETCH_DATA_SUCCESS = 'applicants/fetch-data-success';
const REQUEST_ERROR = 'applicants/request-error';
const TOGGLE_PROGRESS_BAR = 'applicants/toggle-progress-bar';
const GOTO_PAGE_SUCCESS = 'applicants/goto-page-success';
const APPLY = 'applicants/apply';
const CLEAR = 'applicants/clear';
const SET_SEARCH_QUERY = 'applicants/set-search-query';

export const allowedPermissions = {
	view: 'affiliates_list',
	edit: 'affiliates_update'
};

const initialState = {
	data: null,
	showProgressBar: false,
	table: table.initialState,
	filters: filters.initialState,
	calendar: calendar.initialState,
	search: '',
	pagination: {
		currentPage: 1,
		perPage: 100,
		total: null
	},
	error: false
};

export const actions = Object.assign(
	{
		fetchData: props => (dispatch, getState) => {
			const externalQuery = qs.parse(_get(props, ['location', 'query'], {}));
			const savedFilters = filters.selectors.getSavedFilters(getState(), 'applicantsList');

			if (_isEmpty(externalQuery) && _get(savedFilters, ['entity', 'filters', 'length'])) {
				return dispatch(replace(`applicants/?${qs.stringify(savedFilters)}`));
			}

			dispatch(request(getState, externalQuery))
				.then(() => {
					if (externalQuery.entity) {
						const filtersList = externalQuery.entity.filters || [];
						dispatch(filters.actions.setFiltersFromExternalQuery(filtersList));
					}
				});
		},

		apply: () => (dispatch, getState) => {
			const searchForm = getFormValues('applicantsSearchForm')(getState());
			const search = searchForm && searchForm.search;
			dispatch({
				type: APPLY,
				payload: search
			});
			dispatch(request(getState));
		},

		search: () => (dispatch, getState) => {
			dispatch(actions.setSearchQuery());
			dispatch(request(getState, dispatch));
		},

		setSearchQuery: () => (dispatch, getState) => {
			const searchForm = getFormValues('applicantsSearchForm')(getState());
			const search = _get(searchForm, ['search'], '');

			dispatch({
				type: SET_SEARCH_QUERY,
				payload: search
			});
		},

		clear: () => dispatch => {
			dispatch(actions.toggleProgressBar());
			dispatch(replace('applicants/'));
			setTimeout(() => dispatch({ type: CLEAR }));
		},

		toggleProgressBar: () => ({
			type: TOGGLE_PROGRESS_BAR
		}),

		gotoPage: page => (dispatch, getState) => {
			dispatch(actions.toggleProgressBar());

			dispatch({
				type: GOTO_PAGE_SUCCESS,
				payload: page
			});

			request(getState)(dispatch);
		}

	},
	table.actions,
	filters.actions,
	calendar.actions
);

const reducers = Object.assign(
	{
		[LOCATION_CHANGE]: (state, action) => {
			if (action.payload.pathname.indexOf('applicants/') !== -1) {
				return {
					...state,
					pagination: {
						...state.pagination,
						currentPage: initialState.pagination.currentPage
					},
					data: null
				};
			}
			return initialState;
		},
		[FETCH_DATA_SUCCESS]: (state, action) => ({
			...state,
			data: action.payload.data.rows,
			pagination: {
				...state.pagination,
				total: action.payload.data.total
			},
			showProgressBar: false,
			error: false
		}),
		[APPLY]: (state, action) => ({
			...state,
			pagination: initialState.pagination,
			search: action.payload,
			showProgressBar: true
		}),
		[CLEAR]: () => initialState,
		[GOTO_PAGE_SUCCESS]: (state, action) => ({
			...state,
			pagination: {
				...state.pagination,
				currentPage: action.payload
			}
		}),
		[REQUEST_ERROR]: state => ({
			...state, showProgressBar: true
		}),
		[TOGGLE_PROGRESS_BAR]: state => ({ ...state, showProgressBar: true }),
		[SET_SEARCH_QUERY]: (state, action) => ({ ...state, search: action.payload })
	},
	table.reducers,
	filters.reducers,
	calendar.reducers
);

export default handleActions(reducers, _cloneDeep(initialState));

export const selectors = {
	getData: state => state.applicantsList.data,
	getTable: table.selectors.getTable,
	getFilters: state => filters.selectors.getFilters(
		state => state.applicantsList.filters,
		state => state
	)(state),
	getSearchData: state => ({ search: state.applicantsList.search }),
	getPagination: state => {
		const { perPage, total, currentPage } = state.applicantsList.pagination;
		return {
			pages: _range(Math.ceil(total / perPage)),
			currentPage,
			total
		};
	},
	getCalendar: state => calendar.selectors.getCalendar(
		state => state.applicantsList.calendar,
		state => state
	)(state),
	getCan: state => checkPermissions(state, allowedPermissions),
	getIntl: state => intl(state),
	getProgressBar: state => state.applicantsList.showProgressBar,
	getError: state => state.applicantsList.error
};

function request(getState, externalQuery) {
	const {
		search,
		calendar,
		filters: { selectedFilters },
		pagination: { perPage, currentPage },
		table: { sortByColumn }
	} = getState().applicantsList;

	const limit = perPage;
	const offset = (currentPage - 1) * perPage;

	let filters = {};
	const inverseFilters = {};

	if (selectedFilters && !_isEmpty(selectedFilters)) {
		selectedFilters.forEach(filter => {
			const values = filter.value.map(f => f.value);
			if (values.length) {
				const selectedFilterValues = filter.shouldInverse ? inverseFilters : filters;
				selectedFilterValues[filter.name] = values;
			}
		});
	}

	if (externalQuery && externalQuery.query && externalQuery.query.filters) {

		externalQuery.entity.filters && externalQuery.entity.filters.forEach(filter => {
			if (filter.is_inverted && JSON.parse(filter.is_inverted)) {
				inverseFilters[filter.name] = filter.values.map(f => f.value);
			}
		});

		externalQuery.query.filters && _forEach(externalQuery.query.filters, (value, key) => {
			if (inverseFilters[key]) {
				return;
			}
			filters[key] = value;
		});
	}

	const query = qs.stringify({
		offset,
		limit,
		filters: filters,
		inverse_filters: inverseFilters,
		date_range: getDateRangeFromCalendar(calendar),
		search,
		sort: [sortByColumn.index, sortByColumn.direction]
	});

	return http({
		url: `${endpoints.APPLICANTS.BASE}?${query}`,
		method: 'get',
		actions: [FETCH_DATA_SUCCESS, REQUEST_ERROR]
	});

}



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/applicants/list/index.js